import { useEffect } from "react";
import { useFormikContext } from "formik";
import Callout from "../../UI/Callout";
// @ts-expect-error pure js
import useDebouncedValue from "../../../hooks/use-debounced-value";
import useZoneTendue from "../../../hooks/use-zone-tendue";

interface ZoneTendueCalloutValues {
  cp?: string;
  ville?: string;
  zone_tendue?: string;
}

interface ZoneTendueCalloutStatus {
  zoneTendueAppliedFor?: string;
}

/**
 * Détecte la zone tendue d'après l'adresse du bien et l'applique au radio
 * `zone_tendue` tant que l'adresse diffère de celle enregistrée (création ou
 * changement d'adresse). Sur un bien existant à l'adresse inchangée, la
 * détection n'est qu'affichée.
 */
export default function ZoneTendueCallout() {
  const { values, initialValues, setFieldValue, status, setStatus } =
    useFormikContext<ZoneTendueCalloutValues>();
  const cp: string = useDebouncedValue(values.cp ?? "", 400);
  const ville: string = useDebouncedValue(values.ville ?? "", 400);
  const { detection } = useZoneTendue(cp, ville);

  const addressChanged =
    values.cp !== initialValues.cp || values.ville !== initialValues.ville;
  const appliedFor = (status as ZoneTendueCalloutStatus | undefined)
    ?.zoneTendueAppliedFor;
  const detectedCode = detection?.lookup.code_insee;
  const detectedZoneTendue = detection?.lookup.zone_tendue;

  useEffect(() => {
    if (
      detectedCode === undefined ||
      !addressChanged ||
      appliedFor === detectedCode
    ) {
      return;
    }
    setFieldValue("zone_tendue", String(detectedZoneTendue));
    setStatus({ ...status, zoneTendueAppliedFor: detectedCode });
  }, [
    detectedCode,
    detectedZoneTendue,
    addressChanged,
    appliedFor,
    setFieldValue,
    setStatus,
    status,
  ]);

  if (!detection) {
    return null;
  }

  return (
    <Callout variant="info">
      Détecté automatiquement d'après l'adresse ({detection.commune}) : ce
      logement {detection.lookup.zone_tendue ? "est situé" : "n'est pas situé"}{" "}
      en zone tendue.
    </Callout>
  );
}
